import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

const inputCls =
  "w-full rounded-lg border border-[#E2DDD5] bg-[#FAF9F5] px-4 py-2.5 text-sm text-[#0F1B2B] outline-none transition-colors placeholder:text-[#8896AB] focus:border-[#0E8A8A]/60";

export function openLeadForm() {
  window.dispatchEvent(new Event("osh-lead-form"));
}

export default function LeadFormDialog() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", email: "", business: "", message: "" });

  useEffect(() => {
    const handler = () => { setOpen(true); setDone(false); setError(""); };
    window.addEventListener("osh-lead-form", handler);
    return () => window.removeEventListener("osh-lead-form", handler);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/leads", form);
      setDone(true);
      setForm({ name: "", phone: "", email: "", business: "", message: "" });
      setTimeout(() => setOpen(false), 2800);
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md border-[#E2DDD5] bg-white" data-testid="lead-form-modal">
        {done ? (
          <div className="flex flex-col items-center py-10 text-center" data-testid="lead-success">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#0E8A8A]/10">
              <CheckCircle2 size={30} className="text-[#0E8A8A]" />
            </span>
            <h3 className="mt-5 font-syne text-xl font-bold text-[#0F1B2B]">Enquiry received!</h3>
            <p className="mt-2 max-w-xs text-sm text-[#5A6578]">Our team will call you back within 24 hours. Welcome to the Omshubharidhaan family.</p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0E8A8A]">Partner Enquiry</span>
              <DialogTitle className="font-syne text-2xl font-bold text-[#0F1B2B]">Become a Partner</DialogTitle>
              <p className="text-sm text-[#8896AB]">Fill this in — our team calls back within 24 hours.</p>
            </DialogHeader>
            <form onSubmit={submit} className="mt-4 space-y-4" data-testid="lead-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <input required value={form.name} onChange={set("name")} placeholder="Full name *" data-testid="lead-name-input" className={inputCls} />
                <input required value={form.phone} onChange={set("phone")} placeholder="Mobile number *" data-testid="lead-phone-input" className={inputCls} />
              </div>
              <input type="email" value={form.email} onChange={set("email")} placeholder="Email (optional)" data-testid="lead-email-input" className={inputCls} />
              <input value={form.business} onChange={set("business")} placeholder="Business type (e.g. Kirana store)" data-testid="lead-business-input" className={inputCls} />
              <textarea rows={3} value={form.message} onChange={set("message")} placeholder="Which services are you interested in?" data-testid="lead-message-input" className={inputCls} />
              {error && <p className="text-sm text-red-500" data-testid="lead-error">{error}</p>}
              <button type="submit" disabled={loading} data-testid="lead-submit-btn"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[#1B3A5C] py-3.5 text-sm font-bold text-white transition-all duration-300 hover:bg-[#0E8A8A] active:scale-95 disabled:opacity-60">
                {loading ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                Submit Enquiry
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
